import ColorizeRoundedIcon from '@mui/icons-material/ColorizeRounded';
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  InputLabel,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormProvider, RHFTextField, RHFUploadAvatar } from 'src/components/hook-form';
import HttpServices from 'src/services/httpService';
import { imageModeuleUrl, userModuleURL } from 'src/services/urlService';
import ValidationTool from 'src/utils/validationHelper';
import ColorPopover from '../branding/branding-color-popover';

const OrganizationDialog = ({ handleSkip, showToast, successCallback, failureCallback, onBoardingInfo }) => {
  const { t: translate } = useTranslation();
  const maxFileSizeInBytes = 10485760;
  const supportedFileFormat = ['image/jpeg', 'image/png', 'image/jpg'];

  // State Variables
  const [orgName, setOrgName] = useState('');
  const [orgDescription, setOrgDescription] = useState('');
  const [orgPrimaryColor, setOrgPrimaryColor] = useState('');
  const [orgSecondaryColor, setOrgSecondaryColor] = useState('');
  const [orgIcon, setOrgIcon] = useState('');
  const [formSchema, setFormSchema] = useState({});
  const [formValidationErrors, setFormValidationErrors] = useState({});
  const [fileUploadProgress, setFileUploadProgress] = useState(0);
  const [popoverElement, setPopoverElement] = useState(null);
  const [selectedPopOverData, setSelectedPopOverData] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openOrgDialog, setOpenOrgDialog] = useState(true);

  const defaultValues = {
    orgName: orgName || '',
    orgDescription: orgDescription || '',
    orgPrimaryColor: orgPrimaryColor || '',
    orgSecondaryColor: orgSecondaryColor || '',
    orgIcon: orgIcon || '',
  };

  useEffect(() => {
    generateFormModelSchema();
  }, []);

  const generateFormModelSchema = () => {
    let fields = [ValidationTool.GenerateSchema('orgName', translate('organization_dialog.org_name_label'), 'TEXT', true)];
    const schema = ValidationTool.GenerateSchemaModel(fields, translate);
    setFormSchema(schema);
  };

  const handleInputChange = (event) => {
    let value = event.target.value;
    let name = event.target.name;
    switch (name) {
      case 'orgName':
        setOrgName(value);
        break;
      case 'orgDescription':
        setOrgDescription(value);
        break;
      default:
        break;
    }
  };

  const handleDrop = (acceptedFiles, iconType) => {
    const file = acceptedFiles[0];
    handleFileUpload(file, iconType);
  };

  const handleFileUpload = (file, iconType) => {
    let isFileInCorrectFormat = supportedFileFormat.some((a) => a.includes(file.type));
    if (isFileInCorrectFormat) {
      if (file.size < maxFileSizeInBytes) {
        setFileUploadProgress(1);
        let formData = new FormData();
        formData.append('fileupload', file);
        axios
          .post(imageModeuleUrl.uploadMedia, formData, {
            headers: HttpServices.GetRequestHeader('form', true),
            onUploadProgress: (data) => {
              let loadedProgress = Math.round((100 * data.loaded) / data.total);
              setFileUploadProgress(loadedProgress > 90 ? 90 : loadedProgress);
            },
          })
          .then((response) => {
            setFileUploadProgress(100);
            if (response.status === 200 && response.data.http_code === 200)
              fileUploadSuccessCallback(
                response.data.data,
                iconType,
                translate('manual_onboarding.file_uploaded_success', 'File Uploaded Successfully')
              );
            else
              showToast(
                translate(
                  'manual_onboarding.something_went_wrong',
                  'Something went wrong. Please contact our technical team'
                ),
                'e'
              );
            setFileUploadProgress(0);
          })
          .catch((error) => {
            setFileUploadProgress(0);
            showToast(error.toString(), 'e');
          });
      } else
        showToast(
          translate(
            'manual_onboarding.max_file_size',
            'Maximum file size for image is 10MB. Please select image below 10 MB'
          ),
          'e'
        );
    } else {
      showToast(
        translate(
          'manual_onboarding.unsupported_format',
          'We only support JPEG, and PNG file formats. The selected file are not in the supported format'
        ),
        'e'
      );
    }
  };

  const fileUploadSuccessCallback = (data, iconType, message) => {
    if (iconType === 'orgIcon') setOrgIcon(data.media_data[0].media_url);
    showToast(message, 's');
  };

  const methods = useForm({
    defaultValues,
  });

  const { handleSubmit } = methods;

  const onSubmit = () => {
    let orgForm = {
      org_name: orgName,
      org_description: orgDescription,
      org_primary_color: orgPrimaryColor,
      org_secondary_color: orgSecondaryColor,
      org_icon_url: orgIcon,
    };
    let postProps = {
      url: userModuleURL.updateOrg,
      body: orgForm,
      successCallback: (data, message) => successCallback(data, message, 'org'),
      failureCallback: orgInfoFailureCallback,
    };
    setIsSubmitting(true);
    HttpServices.Post(postProps).finally(() => {
      setIsSubmitting(false);
      setOpenOrgDialog(false);
    });
  };
  const orgInfoFailureCallback = (message) => {
    setIsSubmitting(false);
    message && showToast(message, 'e');
  };
  const handleSubmitForm = () => {
    let userForm = {
      orgName: orgName,
    };
    let { formValidationErrors, isValidForm } = ValidationTool.FormValidation(formSchema, userForm);
    if (isValidForm) {
      setFormValidationErrors({});
      onSubmit();
    } else {
      setFormValidationErrors(formValidationErrors);
    }
  };

  const handleOpenMenu = (event, data) => {
    setSelectedPopOverData(data);
    setPopoverElement(event.currentTarget);
  };

  const handleColor = (color, type) => {
    if (type === 'primary') {
      setOrgPrimaryColor(color.hex);
    } else {
      setOrgSecondaryColor(color.hex);
    }
  };

  const handleCloseMenu = () => {
    setPopoverElement(null);
  };

  const colorProps = {
    popoverElement: popoverElement,
    selectedPopOverData: selectedPopOverData,
    handleCloseMenu,
    handleColor,
    color: orgPrimaryColor,
  };

  return (
    <>
      <Dialog
        open={openOrgDialog && !onBoardingInfo?.org_done && !onBoardingInfo?.org_skipped}
        onClose={() => setOpenOrgDialog(false)}
        maxWidth="md"
        fullWidth
      >
        {/* Top-right Skip Button */}
        <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
          <Button variant="contained" color="primary" onClick={handleSkip}>
            {translate('organization_dialog.skip_button')}
          </Button>
        </Box>
        <DialogTitle>{translate('organization_dialog.title')}</DialogTitle>
        <FormProvider methods={methods} onSubmit={handleSubmit(handleSubmitForm)}>
          <DialogContent>
            <Card sx={{ px: 3, textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <InputLabel style={{ paddingBottom: '0.5rem' }}>
                  {translate('manage_branding.branding.header_logo_image')}
                </InputLabel>
                <RHFUploadAvatar
                  name="orgIcon"
                  accept="image/*"
                  maxSize={5242880}
                  onDrop={(e) => {
                    handleDrop(e, 'orgIcon');
                  }}
                  handleDeleteProfiles={() => setOrgIcon('')}
                  url={orgIcon}
                  helperText={
                    <Typography
                      variant="caption"
                      sx={{
                        mt: 2,
                        mx: 'auto',
                        display: 'block',
                        textAlign: 'center',
                        color: 'text.secondary',
                      }}
                      dangerouslySetInnerHTML={{ __html: translate('organization_dialog.avatar_helper_text') }}
                    />
                  }
                />
              </Box>
            </Card>
            <Card sx={{ p: 1, mt: 3 }}>
              <Box
                sx={{
                  display: 'grid',
                  rowGap: 3,
                  columnGap: 2,
                  gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
                }}
              >
                <RHFTextField
                  name="orgName"
                  label={translate('organization_dialog.org_name_label')}
                  value={orgName}
                  onChange={handleInputChange}
                  error={formValidationErrors?.orgName}
                  helperText={formValidationErrors?.orgName}
                />
                <RHFTextField
                  name="orgPrimaryColor"
                  value={orgPrimaryColor}
                  label={translate('organization_dialog.org_primary_color_label')}
                  onChange={handleInputChange}
                  error={formValidationErrors?.orgPrimaryColor}
                  helperText={formValidationErrors?.orgPrimaryColor}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="large"
                          color="inherit"
                          onClick={(e) => {
                            handleOpenMenu(e, 'primary');
                          }}
                        >
                          <ColorizeRoundedIcon />
                        </IconButton>
                        {orgPrimaryColor && (
                          <Box
                            style={{
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              boxShadow: 1,
                              backgroundColor: orgPrimaryColor,
                              marginLeft: 1,
                            }}
                          />
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
                <RHFTextField
                  name="orgSecondaryColor"
                  value={orgSecondaryColor}
                  label={translate('organization_dialog.org_secondary_color_label')}
                  onChange={handleInputChange}
                  error={formValidationErrors?.orgSecondaryColor}
                  helperText={formValidationErrors?.orgSecondaryColor}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="large"
                          color="inherit"
                          onClick={(e) => {
                            handleOpenMenu(e, 'secondary');
                          }}
                        >
                          <ColorizeRoundedIcon />
                        </IconButton>
                        {orgSecondaryColor && (
                          <Box
                            style={{
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              boxShadow: 1,
                              backgroundColor: orgSecondaryColor,
                              marginLeft: 1,
                            }}
                          />
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
                <RHFTextField
                  name="orgDescription"
                  label={translate('organization_dialog.org_description_label')}
                  multiline
                  rows={5}
                  value={orgDescription}
                  onChange={handleInputChange}
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={isSubmitting}
                  sx={{ minWidth: 120 }}
                >
                  {translate('organization_dialog.submit_button')}
                </Button>
              </Box>
            </Card>
          </DialogContent>
        </FormProvider>
        {/* Remove DialogActions since Skip Button is moved */}
      </Dialog>
      <ColorPopover {...colorProps} />
    </>
  );
};

export default OrganizationDialog;